import classNames from "classnames";
import { Url } from "next/dist/shared/lib/router/router";
import Link from "next/link";
import { useRouter } from "next/router";
import { Fragment, useMemo } from "react";

import IconInclinedStar from "@/icons/icon-inclined-star";

import Button from "../common/button";
import HighlightedText from "../common/highlighted-text";
import Icon from "../common/icon";
import JsonLd from "../common/json-ld";
import styles from "./product-breadcrumbs.module.scss";

export type Breadcrumb = {
  text: string;
  url?: Url | null;
  onClick?: () => void;
};

type Props = {
  breadcrumbs: Breadcrumb[];
  className?: string;
};

const getBreadcrumbSchemaData = (breadcrumbs: Breadcrumb[], urlPrefix: string): string => {
  return JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      position: ++index,
      name: item.text,
      item: urlPrefix + (item.url || ""),
    })),
  });
};

export default function Breadcrumbs(props: Props) {
  const router = useRouter();

  const breadcrumbSchemaData = useMemo(
    () =>
      props.breadcrumbs && router.locale
        ? getBreadcrumbSchemaData(props.breadcrumbs, `https://www.kikocosmetics.com/${router.locale}`)
        : undefined,
    [props.breadcrumbs, router.locale]
  );

  if (props.breadcrumbs.length == 0) {
    return <></>;
  }

  return (
    <>
      {breadcrumbSchemaData && <JsonLd data={breadcrumbSchemaData} />}
      <div className={classNames(styles.breadCrumbs, props.className)}>
        {props.breadcrumbs.map((c, i) => (
          <Fragment key={i}>
            {!!i && <Icon icon={IconInclinedStar} height={6} className={styles.breadCrumbsSeparator} aria-hidden />}
            {c.url ? (
              <Link prefetch={false} href={c.url} className={styles.breadCrumb} onClick={() => c.onClick?.()}>
                <HighlightedText text={c.text} />
              </Link>
            ) : c.onClick ? (
              <Button className={styles.breadCrumb} onClick={() => c.onClick?.()}>
                <HighlightedText text={c.text} />
              </Button>
            ) : (
              <div className={styles.breadCrumb}>
                <HighlightedText text={c.text} />
              </div>
            )}
          </Fragment>
        ))}
      </div>
    </>
  );
}
